import React from "react";

/**
 * Platform Schedule Side labels + colors (Channel Programming parity):
 * Staging = Yellow, Pending Update = Blue, Production (Live) = Green.
 *
 * Living operator sides only: staging | pending | production.
 * Never render "Unknown" — detached/historical schedules are not a Schedule Side.
 *
 * Placement display: Channel name with Schedule Side badge to its right,
 * then Rotation on the next line when shown.
 * Machine values stay staging|pending|production; operator label for pending
 * is "Pending Update" (publish snapshot awaiting Go Live / Cancel).
 */

export type ScheduleSide = "staging" | "pending" | "production";

function isDevRuntime(): boolean {
  return process.env.NODE_ENV !== "production";
}

/**
 * Normalize to a living Schedule Side, or null when absent/invalid.
 * Invalid values fail loud in development; production logs and returns null
 * (never "unknown").
 */
export function normalizeScheduleSide(
  raw?: string | null,
): ScheduleSide | null {
  if (raw == null) return null;
  const s = String(raw).trim().toLowerCase();
  if (!s) return null;
  if (s === "staging") return "staging";
  if (s === "pending") return "pending";
  if (s === "production" || s === "live") return "production";
  const msg = `[schedule-side] invalid operator Schedule Side: ${JSON.stringify(raw)}`;
  if (isDevRuntime()) {
    throw new Error(msg);
  }
  // eslint-disable-next-line no-console
  console.error(msg);
  return null;
}

export function scheduleSideLabel(side: ScheduleSide | string | null | undefined): string {
  const n = normalizeScheduleSide(side == null ? side : String(side));
  if (n === "staging") return "Staging";
  if (n === "pending") return "Pending Update";
  if (n === "production") return "Production";
  return "—";
}

const SIDE_STYLES: Record<
  ScheduleSide,
  { background: string; color: string; border: string }
> = {
  staging: {
    background: "#fff3cd",
    color: "#856404",
    border: "#ffc107",
  },
  pending: {
    background: "#cfe2ff",
    color: "#084298",
    border: "#0d6efd",
  },
  production: {
    background: "#d1e7dd",
    color: "#0f5132",
    border: "#198754",
  },
};

export function ScheduleSideBadge({
  side,
  compact,
}: {
  side?: string | null;
  compact?: boolean;
}): React.ReactElement | null {
  const normalized = normalizeScheduleSide(side);
  if (!normalized) return null;
  const palette = SIDE_STYLES[normalized];
  const label = scheduleSideLabel(normalized);
  return (
    <span
      data-testid={`schedule-side-${normalized}`}
      title={`Schedule: ${label}`}
      style={{
        display: "inline-block",
        padding: compact ? "0 6px" : "1px 8px",
        borderRadius: 4,
        border: `1px solid ${palette.border}`,
        background: palette.background,
        color: palette.color,
        fontSize: compact ? 10 : 11,
        fontWeight: 700,
        lineHeight: compact ? "16px" : "18px",
        whiteSpace: "nowrap",
        verticalAlign: "middle",
      }}
    >
      {label}
    </span>
  );
}

/**
 * Canonical placement: Channel + Schedule badge (badge to the right),
 * then Rotation below when shown.
 * Use for confirm dialogs and any non-table placement list.
 */
export function SchedulePlacementStack({
  scheduleSide,
  channel,
  rotation,
  compact,
  showRotation = true,
}: {
  scheduleSide?: string | null;
  channel?: string | null;
  rotation?: string | null;
  compact?: boolean;
  /** When false, omit the Rotation line (e.g. section headers). */
  showRotation?: boolean;
}): React.ReactElement {
  return (
    <div
      data-testid="schedule-placement-stack"
      style={{ lineHeight: 1.45 }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: compact ? 6 : 8,
        }}
      >
        <span>{channel || "—"}</span>
        {scheduleSide ? (
          <ScheduleSideBadge side={scheduleSide} compact={compact} />
        ) : null}
      </div>
      {showRotation ? <div>{rotation || "—"}</div> : null}
    </div>
  );
}
